'use client';

import { useEffect, useRef } from 'react';
import { useAppStore } from '@/features/shared/app-store';
import { useCurrencyStore } from '@/features/shared/currency-store';
import { fetchCart } from '@/features/cart/internal/helpers/fetch-cart';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { CurrencyCode } from '@/shared/constants/currencies';
import { setBestGuessAddress } from '@/features/cart/internal/helpers/set-best-guess-address';
import { CartData } from '@/features/cart/shared/types/cart.types';

export default function CartStoreHydrator() {
  const setCart = useAppStore((s) => s.setCart);
  const updateCartCurrency = useAppStore((s) => s.updateCurrency);
  const currencyCode = useCurrencyStore((s) => s.currentCurrency);

  const previousCurrency = useRef<CurrencyCode | null>(null);
  const previousWildMemberStatus = useRef<boolean | null>(null);
  const previousLoggedInStatus = useRef<boolean | null>(null);
  const { loading, authenticatedUser } = useUser();

  useEffect(() => {
    if (loading || !currencyCode) return;
    const currentWildMember = !!authenticatedUser?.isWildMember;
    const currentLoginStatus = !!authenticatedUser;
    const needsFirstFetch = previousLoggedInStatus.current === null;

    const membershipChanged =
      previousWildMemberStatus.current !== null &&
      previousWildMemberStatus.current !== currentWildMember;

    const loginStatusChanged =
      previousLoggedInStatus.current !== null &&
      previousLoggedInStatus.current !== currentLoginStatus;

    if (!needsFirstFetch && !membershipChanged && !loginStatusChanged) return;

    previousWildMemberStatus.current = authenticatedUser?.isWildMember || false;
    previousLoggedInStatus.current = !!authenticatedUser;

    (async () => {
      const emptyCart: CartData = {
        items: [],
        totalPrice: {
          type: 'total',
          value: 0,
          currencyCode,
        },
        cartId: null,
        currencyCode,
      };
      try {
        if (loginStatusChanged) {
          await setBestGuessAddress();
        }
        const cart = await fetchCart();
        setCart(cart ?? emptyCart);
      } catch (_) {
        console.debug('no stored cart', _);
        setCart(emptyCart);
      }
    })();
  }, [currencyCode, loading, authenticatedUser, setCart, updateCartCurrency]);

  useEffect(() => {
    if (!currencyCode) return;
    if (previousCurrency.current === null) {
      previousCurrency.current = currencyCode;
      return;
    }
    if (previousCurrency.current === currencyCode) return;
    previousCurrency.current = currencyCode;
    updateCartCurrency(currencyCode);
  }, [currencyCode, updateCartCurrency]);
  return null;
}
